import { type Dispatch, type FC, type SetStateAction, useMemo } from 'react'
import { GalleryModal } from '@/components/commons/gallery-modal'
import { GalleryModalHeader } from '@/components/commons/gallery-modal-header'
import { GalleryModalSidebar } from '@/components/commons/gallery-modal-sidebar'
import { GalleryModalThumbnails } from '@/components/commons/gallery-modal-thumbnails'
import { GalleryNeedsReviewSidebar } from '@/features/photoset/components/gallery-photoset-modal/gallery-needs-review-modal/gallery-needs-review-sidebar'
import { ImageBadge } from '@/features/photoset/components/image-badge'
import { useGalleryNeedsReviewShortcuts } from '@/features/photoset/hooks/use-gallery-needs-review-shortcuts'
import type { Image, Space } from '@/features/photoset/types'
import { spaceNameToLabel } from '@/features/photoset/utils'
import { useTranslation } from '@/translations/i18n'

interface GalleryNeedsReviewModalProps {
	selectableImages: Omit<Image, 'space'>[]
	currentDetailImageIndex: number
	setCurrentDetailImageIndex: Dispatch<SetStateAction<number | null>>
	onClose: () => void
	currentSpaceName: Space['key_name']
}

export const GalleryNeedsReviewModal: FC<GalleryNeedsReviewModalProps> = ({
	selectableImages,
	currentDetailImageIndex,
	setCurrentDetailImageIndex,
	onClose,
	currentSpaceName
}) => {
	const { t } = useTranslation()
	const currentImage = useMemo(
		() => selectableImages[currentDetailImageIndex],
		[currentDetailImageIndex, selectableImages]
	)

	useGalleryNeedsReviewShortcuts({
		selectableImages,
		currentDetailImageIndex,
		setCurrentDetailImageIndex,
		onClose
	})

	return (
		<GalleryModal
			currentImage={currentImage}
			headerSlot={
				<GalleryModalHeader
					currentImage={currentImage}
					endSlot={<ImageBadge type='to_revise' />}
					kickerTitle={spaceNameToLabel[currentSpaceName]}
					onClose={onClose}
				/>
			}
			onClose={onClose}
			sidebarSlot={
				<GalleryModalSidebar
					content={
						<GalleryNeedsReviewSidebar
							currentImage={currentImage}
						/>
					}
					title={t('common:feedback')}
				/>
			}
			thumbnails={
				currentImage?.retouched_thumbnails ||
				currentImage?.ai_retouched_thumbnails
			}
			thumbnailsSlot={
				<GalleryModalThumbnails
					currentImageId={currentImage.id}
					images={selectableImages}
					onSelect={(_, index) => {
						setCurrentDetailImageIndex(index)
					}}
					thumbnails={'retouched_thumbnails'}
					thumbnailsFallback={'ai_retouched_thumbnails'}
				/>
			}
		/>
	)
}
